import { useState, type ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  BellRing,
  Bus,
  Check,
  Cloud,
  Compass,
  Headphones,
  Languages,
  LayoutGrid,
  Monitor,
  ShieldCheck,
  Smartphone,
  Speaker,
  Volume2,
  type LucideIcon,
} from 'lucide-react'
import { CONTACT_INFO, productPath } from '@/lib/constants'
import { AUDIENCES, PRODUCTS, getProductById, type Audience } from '@/lib/products'
import { cn } from '@/lib/utils'
import Reveal from '@/components/shared/Reveal'
import SectionHeader from '@/components/shared/SectionHeader'
import ProductCard from '@/components/shared/ProductCard'
import CtaBand from '@/components/shared/CtaBand'
import { ProductIconTile, StatusBadge } from '@/components/shared/ProductBits'
import AppLauncher from '@/components/layout/AppLauncher'

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Tdrem',
  url: 'https://www.tdrem.com',
  logo: 'https://www.tdrem.com/images/tdrem-logo.png',
  email: CONTACT_INFO.email,
  areaServed: 'IL',
  slogan: 'Think · Develop · REMaster',
}

const AUDIENCE_ICONS: Record<Audience, LucideIcon> = {
  school: BellRing,
  mosque: Volume2,
  transport: Bus,
  careers: Compass,
  crypto: ShieldCheck,
}

export default function Home() {
  const { t } = useTranslation(['home', 'products', 'common'])

  return (
    <>
      <Helmet>
        <title>{`${t('meta.title')} | Tdrem`}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://www.tdrem.com/" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <Hero />
      <ProductFinder />
      <Values />
      <AudioSpotlight />
      <CtaBand />
    </>
  )
}

/* ================================================================ Hero */
function Hero() {
  const { t } = useTranslation(['home', 'products', 'common'])

  return (
    <section className="relative -mt-16 overflow-hidden pt-16">
      <div aria-hidden className="bg-grid absolute inset-0 -z-10" />
      <div aria-hidden className="absolute -top-40 start-1/2 -z-10 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-gradient-to-br from-brand-blue/15 via-brand-cyan/10 to-transparent blur-3xl rtl:translate-x-1/2" />

      <div className="container grid grid-cols-1 items-center gap-10 py-10 sm:py-14 md:py-20 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:py-24">
        <div className="space-y-6 sm:space-y-7 animate-fade-in">
          <span className="eyebrow">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-3xl font-extrabold leading-[1.12] sm:text-5xl sm:leading-[1.08] lg:text-6xl">
            {t('hero.title')} <span className="text-gradient-brand">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg md:text-xl">{t('hero.subtitle')}</p>

          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <a
              href="#products"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-primary px-6 font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-colors hover:bg-primary/90"
            >
              {t('common:actions.explore')}
              <ArrowRight className="h-4 w-4 flip-rtl" />
            </a>
            <AppLauncher className="h-12 justify-center rounded-xl px-5" />
          </div>

          <ul className="flex flex-wrap gap-x-5 gap-y-2 pt-1 text-sm text-muted-foreground sm:gap-x-6 sm:pt-2">
            {(t('hero.proof', { returnObjects: true }) as string[]).map((p) => (
              <li key={p} className="flex items-center gap-1.5">
                <Check className="h-4 w-4 shrink-0 text-emerald-500" />
                {p}
              </li>
            ))}
          </ul>
        </div>

        {/* Product hub panel — doubles as quick navigation */}
        <div className="relative animate-fade-in [animation-delay:150ms]">
          <div className="surface relative overflow-hidden p-2 shadow-lift">
            <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-2.5 sm:px-4 sm:pt-3">
              <span className="text-sm font-semibold">{t('hero.panelTitle')}</span>
              <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                {t('hero.panelCount', { n: PRODUCTS.length })}
              </span>
            </div>
            <ul className="space-y-1">
              {PRODUCTS.map((p) => (
                <li key={p.id}>
                  <Link
                    to={productPath(p.slug)}
                    className="group flex items-start gap-3 rounded-2xl p-2.5 transition-colors hover:bg-accent sm:items-center sm:p-3"
                  >
                    <ProductIconTile product={p} />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2">
                        <span className="font-semibold">{t(`products:${p.id}.name`)}</span>
                        <span className={cn('text-xs font-medium', p.accent.text)}>{t(`products:${p.id}.category`)}</span>
                      </span>
                      <span className="mt-0.5 block line-clamp-2 text-sm text-muted-foreground sm:mt-0 sm:truncate">
                        {t(`products:${p.id}.menuLine`)}
                      </span>
                    </span>
                    <StatusBadge status={p.status} className="hidden sm:inline-flex" />
                    <ArrowRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground opacity-60 transition-all group-hover:opacity-100 sm:mt-0 sm:opacity-0 flip-rtl" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* floating "live" toast */}
          <div className="absolute -bottom-6 start-6 hidden animate-float items-center gap-3 rounded-2xl border bg-card px-4 py-3 shadow-lift sm:flex">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-white">
              <BellRing className="h-4 w-4" />
            </span>
            <span className="text-sm">
              <span className="block font-semibold">{t('hero.toastTitle')}</span>
              <span className="block text-xs text-muted-foreground">{t('hero.toastBody')}</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ====================================================== Product finder */
function ProductFinder() {
  const { t } = useTranslation(['home', 'products', 'common'])
  const [audience, setAudience] = useState<Audience | 'all'>('all')

  const [featured, ...rest] = PRODUCTS
  const isDimmed = (a: Audience) => audience !== 'all' && audience !== a
  const match = audience === 'all' ? null : PRODUCTS.find((p) => p.audience === audience)

  return (
    <section id="products" className="container py-16 md:py-28">
      <Reveal>
        <SectionHeader eyebrow={t('finder.eyebrow')} title={t('finder.title')} subtitle={t('finder.subtitle')} />
      </Reveal>

      {/* Audience chips */}
      <Reveal delay={80} className="mt-8 sm:mt-10">
        <div role="tablist" aria-label={t('finder.title')} className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 md:mx-0 md:flex-wrap md:justify-center md:px-0">
          <Chip active={audience === 'all'} onClick={() => setAudience('all')} icon={LayoutGrid}>
            {t('finder.all')}
          </Chip>
          {AUDIENCES.map((a) => (
            <Chip key={a} active={audience === a} onClick={() => setAudience(a)} icon={AUDIENCE_ICONS[a]}>
              {t(`finder.audiences.${a}`)}
            </Chip>
          ))}
        </div>
        <p className="mt-4 min-h-6 text-center text-sm text-muted-foreground" aria-live="polite">
          {match &&
            t('finder.match', {
              product: t(`products:${match.id}.name`),
            })}
        </p>
      </Reveal>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Reveal className="lg:col-span-2">
          <ProductCard product={featured} featured dimmed={isDimmed(featured.audience)} />
        </Reveal>
        {rest.map((p, i) => (
          <Reveal key={p.id} delay={(i % 2) * 80}>
            <ProductCard product={p} dimmed={isDimmed(p.audience)} className="h-full" />
          </Reveal>
        ))}
      </div>
    </section>
  )
}

function Chip({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon: LucideIcon
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-all',
        active
          ? 'border-foreground bg-foreground text-background shadow-sm'
          : 'bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground'
      )}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  )
}

/* ============================================================== Values */
const VALUE_ICONS = [Cloud, Smartphone, Languages, Headphones]

function Values() {
  const { t } = useTranslation('home')
  const items = t('values.items', { returnObjects: true }) as { title: string; description: string }[]

  return (
    <section className="border-y bg-muted/40">
      <div className="container py-16 md:py-24">
        <Reveal>
          <SectionHeader eyebrow={t('values.eyebrow')} title={t('values.title')} />
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:mt-12 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, i) => {
            const Icon = VALUE_ICONS[i] ?? Cloud
            return (
              <Reveal key={item.title} delay={i * 70}>
                <div className="surface h-full p-6">
                  <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mb-2 text-lg font-semibold">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                </div>
              </Reveal>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* =================================================== Audio spotlight */
const STEP_ICONS = [Smartphone, Cloud, Monitor, Speaker]

function AudioSpotlight() {
  const { t } = useTranslation(['home', 'products', 'common'])
  const steps = t('audio.steps', { returnObjects: true }) as { title: string; description: string }[]
  const bell = getProductById('bell')!
  const mosque = getProductById('mosque')!

  return (
    <section className="container py-16 md:py-28">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-12">
        <Reveal className="space-y-6">
          <SectionHeader align="start" eyebrow={t('audio.eyebrow')} title={t('audio.title')} subtitle={t('audio.subtitle')} />
          <div className="flex flex-wrap gap-3">
            {[bell, mosque].map((p) => (
              <Link
                key={p.id}
                to={productPath(p.slug)}
                className="group inline-flex items-center gap-3 rounded-2xl border bg-card p-2 pe-4 shadow-soft transition-colors hover:bg-accent"
              >
                <ProductIconTile product={p} size="sm" />
                <span className="font-medium">{t(`products:${p.id}.name`)}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 flip-rtl" />
              </Link>
            ))}
          </div>
        </Reveal>

        <Reveal delay={100}>
          <ol className="relative grid grid-cols-1 gap-4 sm:grid-cols-2">
            {steps.map((s, i) => {
              const Icon = STEP_ICONS[i] ?? Cloud
              return (
                <li key={s.title} className="surface relative p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-emerald-600 text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-4xl font-extrabold text-muted-foreground/20">0{i + 1}</span>
                  </div>
                  <h3 className="mb-1.5 font-semibold">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{s.description}</p>
                </li>
              )
            })}
          </ol>
        </Reveal>
      </div>
    </section>
  )
}
